import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';

import { AddAutomationButton } from '../../AutomationList/components/AddAutomationButton';
import { ATSAutomationCreateTestIds } from 'data-testids/ATS';
import {
  AutomationModalActionType,
  AutomationModalPaneOptions,
  AutomationTrigger,
} from 'features/Automations/enums';
import { CreateAutomationCTAButtons } from './CreateAutomationCTAButtons';
import { CreateFormProps } from 'features/Automations/types';
import { ErrorText } from 'features/Automations/styles';
import { getFormDefaultValues } from 'features/Automations/utils';
import { IndividualAutomation } from './IndividualAutomation';
import { QuestionComboBox } from './QuestionComboBox';
import { useAutomation } from 'features/Automations/context/AutomationContext';
import { Question } from 'types/role';

export const CreateForm: React.FC<CreateFormProps> = () => {
  const { roleId, questions, onChangeModalPane } = useAutomation();
  const methods = useForm({
    defaultValues: getFormDefaultValues(roleId),
    mode: 'onSubmit',
    criteriaMode: 'firstError',
    reValidateMode: 'onChange',
  });
  const {
    control,
    setValue,
    handleSubmit,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = methods;
  const { fields, append } = useFieldArray({ control, name: 'automations' });
  const hasSelectedQuestion = Boolean(
    watch('automations.0.trigger.question_id') || fields.length > 1
  );

  const handleQuestionSelection = (data: Question) => {
    if (!data?.id) return;

    const { id, question_type } = data;
    setValue('automations.0.trigger.question_type', question_type);
    setValue('automations.0.trigger.question_id', id);
    trigger('automations.0');
  };

  const onSave = (data) => {
    onChangeModalPane(AutomationModalPaneOptions.LIST);
    console.log('Form to submit:', data.automations);
  };

  const handleAddAutomationClick = () => {
    append({
      role_id: roleId,
      order: fields.length,
      trigger: {
        trigger_type: AutomationTrigger.QUESTION_ANSWER,
        question_id: watch('automations.0.trigger.question_id'),
        question_type: watch('automations.0.trigger.question_type'),
        expected_answer: 'Yes',
      },
      action: {
        action_type: AutomationModalActionType.UPDATE_STATUS,
      },
    });
    onChangeModalPane(AutomationModalPaneOptions.CREATE);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSave)}>
        <Controller
          name={'automations.0'}
          control={control}
          rules={{
            required: true,
            validate: (value) => {
              if (!value?.trigger?.question_id) {
                return 'Please select a question';
              }
              return true;
            },
          }}
          render={({ fieldState: { error } }) => {
            return (
              <>
                <QuestionComboBox
                  onChange={(_event, data, reason) => {
                    if (reason === 'clear') {
                      reset(getFormDefaultValues(roleId));
                    }

                    return handleQuestionSelection(data);
                  }}
                  options={questions}
                />
                {error && (
                  <ErrorText data-testid={ATSAutomationCreateTestIds.MISSING_QUESTION_ERROR_TEXT}>
                    {error.message}
                  </ErrorText>
                )}
              </>
            );
          }}
        />
        {hasSelectedQuestion && (
          <>
            {fields.map((item, index) => {
              return (
                <li key={item.id} style={{ listStyle: 'none' }}>
                  <IndividualAutomation
                    fieldName={`automations.${index}.trigger.expected_answer`}
                    index={index}
                  />
                </li>
              );
            })}
            <AddAutomationButton onClick={handleAddAutomationClick} />
          </>
        )}

        <CreateAutomationCTAButtons hasError={Object.keys(errors).length > 0} />
      </form>
    </FormProvider>
  );
};
